import { KYC_ITEMS, type KycItem } from './constants';
import { isVerifiedRef } from './financial-bar';
import type { Case, FinancialRef } from './initial-cases';

// #772 — HOW FAR AN ITEM IS TOWARDS ITS EVIDENCE MINIMUM, and what follows
// from that on the KYC tab and the Decision tab.
//
// The minimums are on `KYC_ITEMS` (constants.ts). The API recounts the same
// way before an approval (apps/api/src/lib/kyc-items.ts), from the evidence and
// references rather than the `verified` flag, so what this file says the
// analyst still needs is what the server will refuse for. The counting
// predicates below are pinned against the API's by disk-read tests on both
// sides (docs/CROSS_PACKAGE_CONSTANTS.md).
//
// GRANDFATHERED CASES (`kycGrandfathered`, set by A028 on every case that had
// entered vetting before #772 shipped) keep the old rule: the box ticks with
// whatever evidence there is, and no approval is held back. Every other case
// — including a case object that does not carry the flag at all — gets the
// minimums: the strict reading is the safe one to fall into.

export interface KycProgress {
  records: number;
  sources: number;
  met: boolean;
}

// Two records from "PACER" and "pacer " are one source.
export function sourceKey(source: unknown): string {
  return String(source ?? '').trim().toLowerCase();
}

export function kycMinimumsApply(c: Pick<Case, 'kycGrandfathered'>): boolean {
  return c.kycGrandfathered !== true;
}

export function kycItemProgress(
  item: KycItem,
  field: { evidence?: unknown } | undefined | null,
  refs: readonly FinancialRef[] | undefined | null,
): KycProgress {
  let records: number;
  let sources: number;
  if (item.counts === 'financialRefs') {
    records = (refs ?? []).filter(isVerifiedRef).length;
    sources = 0;
  } else {
    const list = Array.isArray(field?.evidence) ? field.evidence : [];
    const evidence = list.filter((e): e is { source?: unknown } => typeof e === 'object' && e !== null);
    records = evidence.length;
    sources = new Set(evidence.map((e) => sourceKey(e.source)).filter((k) => k !== '')).size;
  }
  return { records, sources, met: records >= item.minRecords && sources >= item.minSources };
}

// "1 of 2 records · 1 of 2 sources". Financial Stability counts references.
export function progressText(item: KycItem, p: KycProgress): string {
  if (item.counts === 'financialRefs') {
    return `${p.records} of ${item.minRecords} verified financial reference${item.minRecords === 1 ? '' : 's'}`;
  }
  const records = `${p.records} of ${item.minRecords} record${item.minRecords === 1 ? '' : 's'}`;
  return item.minSources > 0
    ? `${records} · ${p.sources} of ${item.minSources} source${item.minSources === 1 ? '' : 's'}`
    : records;
}

// Why the box cannot be ticked yet, or null when it can. Each record from a new
// source is also a new record, so a shortfall in sources is said as records
// from a different source.
export function shortfallReason(item: KycItem, p: KycProgress): string | null {
  if (p.met) return null;
  const needRecords = Math.max(0, item.minRecords - p.records);
  const needSources = Math.max(0, item.minSources - p.sources);
  const plural = (n: number, word: string) => `${n} more ${word}${n === 1 ? '' : 's'}`;
  if (item.counts === 'financialRefs') {
    return `needs ${plural(needRecords, 'verified financial reference')} — record the call on a reference below`;
  }
  // A one-source item with nothing on it just needs a record; "from a
  // different source" is said only where there is a source to differ from, or
  // the item needs more than one.
  if (needSources > 0 && p.records === 0 && item.minSources === 1) {
    return `needs ${plural(needRecords, 'record')}`;
  }
  if (needSources > 0 && needRecords <= needSources) {
    return `needs ${plural(needSources, 'record')} from ${needSources === 1 ? 'a different source' : 'different sources'}`;
  }
  if (needSources > 0) {
    return `needs ${plural(needRecords, 'record')}, ${needSources} from ${needSources === 1 ? 'a different source' : 'different sources'}`;
  }
  return `needs ${plural(needRecords, 'record')}`;
}

export interface UnmetKycItem {
  key: string;
  label: string;
  progress: string;
}

// Every REQUIRED item short of its minimum, in checklist order — the list the
// API's 422 "KYC incomplete" would name. Empty on a grandfathered case.
export function unmetRequiredKyc(c: Case): UnmetKycItem[] {
  if (!kycMinimumsApply(c)) return [];
  const unmet: UnmetKycItem[] = [];
  for (const [key, item] of Object.entries(KYC_ITEMS)) {
    if (!item.required) continue;
    const p = kycItemProgress(item, c.kyc?.[key], c.financialRefs);
    if (!p.met) unmet.push({ key, label: item.label, progress: progressText(item, p) });
  }
  return unmet;
}
